import React, { createContext, useContext, useState, useCallback } from 'react';
import noticeService from '../services/noticeService';
import { getEcho } from '../echo';

const NoticeContext = createContext();

export const useNotice = () => {
    const context = useContext(NoticeContext);
    if (!context) {
        throw new Error('useNotice must be used within a NoticeProvider');
    }
    return context;
};

export const NoticeProvider = ({ children }) => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTeamId, setActiveTeamId] = useState(null);

    const fetchNotices = useCallback(async (teamId, page = 1, isLoadMore = false) => {
        if (page === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            const response = await noticeService.getNotices(teamId, page);
            if (response.status && response.data.notices) {
                // Handle both raw paginator and resource-wrapped paginator
                const noticesData = response.data.notices;
                const data = noticesData.data || noticesData;
                const lastPage = noticesData.meta?.last_page || noticesData.last_page || 1;

                if (isLoadMore) {
                    setNotices(prev => [...prev, ...data]);
                } else {
                    setNotices(data);
                }

                setHasMore(page < lastPage);
                setCurrentPage(page);
                setActiveTeamId(teamId);
            }
        } catch (error) {
            console.error('Failed to fetch notices:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    const addNotice = useCallback(async (data) => {
        try {
            const response = await noticeService.createNotice(data);
            if (response.status && response.data.notice) {
                // If we're on the first page or the team matches, add it to the top
                if (activeTeamId === parseInt(data.team_id)) {
                    setNotices(prev => [response.data.notice, ...prev]);
                }
                return response;
            }
        } catch (error) {
            console.error('Failed to create notice:', error);
            throw error;
        }
    }, [activeTeamId]);

    const updateNotice = useCallback(async (id, data) => {
        try {
            const response = await noticeService.updateNotice(id, data);
            if (response.status && response.data.notice) {
                setNotices(prev => prev.map(n => n.id === id ? response.data.notice : n));
                return response;
            }
        } catch (error) {
            console.error('Failed to update notice:', error);
            throw error;
        }
    }, []);

    const removeNotice = useCallback(async (id) => {
        try {
            const response = await noticeService.deleteNotice(id);
            if (response.status) {
                setNotices(prev => prev.filter(notice => notice.id !== id));
                return response;
            }
        } catch (error) {
            console.error('Failed to delete notice:', error);
            throw error;
        }
    }, []);

    // Real-time update handler
    const handleNoticeCreated = useCallback((newNotice) => {
        if (activeTeamId === parseInt(newNotice.team_id)) {
            setNotices(prev => {
                // Avoid duplicates
                if (prev.some(n => n.id === newNotice.id)) return prev;
                return [newNotice, ...prev];
            });
        }
    }, [activeTeamId]);

    // Subscribe to real-time events when activeTeamId changes
    React.useEffect(() => {
        if (!activeTeamId) return;

        const echo = getEcho();
        if (!echo) return;

        const channel = echo.private(`team.${activeTeamId}`);

        channel.listen('.notice.created', (data) => {
            console.log('Real-time notice received:', data);
            if (data.notice) {
                handleNoticeCreated(data.notice);
            }
        });

        return () => {
            channel.stopListening('.notice.created');
        };
    }, [activeTeamId, handleNoticeCreated]);


    const value = {
        notices,
        loading,
        loadingMore,
        hasMore,
        currentPage,
        activeTeamId,
        fetchNotices,
        addNotice,
        updateNotice,
        removeNotice,
        handleNoticeCreated,
        setActiveTeamId
    };

    return (
        <NoticeContext.Provider value={value}>
            {children}
        </NoticeContext.Provider>
    );
};
